import { Coordinate, LightMeta, LightsContainer } from "game-common/models"

/**
 * The campfire's light in the dark, alive.
 *
 * The flame art has always moved; the hole it cuts in the night mask did not. It was whatever radius
 * the server last sent, which is a STEP every five seconds as the fire burns down (campfire.ts) and a
 * jump straight back to full when it is fed. Three things on top of that, all client-side:
 *
 * 1. BREATH. The radius and intensity swell and ebb with the flame's own flicker (FireSprite.breath,
 *    the same curve that drives its halo), so the edge of the firelight moves with the fire. Small on
 *    purpose: a light that visibly pumps reads as a strobe, not a fire.
 * 2. SMOOTH BURN-DOWN. The base eases toward each new server value instead of snapping, so the dark
 *    closes in on the camp continuously.
 * 3. FLARE. Feeding or lighting the fire throws the light out past its full reach and lets it settle
 *    back (FireSprite.flareLevel; the client hears campfire_fed / campfire_lit). It is the reward for
 *    the camp's most repeated chore.
 *
 * WHEN IT ANIMATES. Only while the room is dark AND the fire was drawn this frame. Every write to the
 * LightsContainer re-bakes the mask, so a breathing fire costs a bake per frame -- the same cost as
 * walking, already paid on every moving frame. A fire off screen or in daylight just carries its
 * server value, written only when that changes, as before.
 *
 * LIVE TUNING: window.firelightBreath = 1 (0 stills it), window.firelightFlare = 1.
 */

export interface FirelightSource {
    /** -1..1 (more while sputtering): where the flame is in its flicker right now. */
    breath(nowMs: number): number
    /** 0..1: how far into a feed/light flare the fire is. */
    flareLevel(nowMs: number): number
}

// How far the breath moves the light, as fractions of its current radius and intensity. At the
// campfire's 60px that is about +-4px of edge under a 50px blur: felt more than seen.
export const BREATH_RADIUS = 0.07
export const BREATH_INTENSITY = 0.06
// The flare's reach past full strength at its peak.
export const FLARE_RADIUS = 0.45
export const FLARE_INTENSITY = 0.1
// Easing time constants, in seconds. Down slowly, so a 5-second server step becomes a drift; up fast,
// because the only way up is being fed, and the flare already covers that moment.
const SHRINK_TAU = 1.6
const GROW_TAU = 0.25
// A long gap (a backgrounded tab, a hitch) must not be integrated as one huge step.
const MAX_DT = 0.25

interface State {
    radius: number
    intensity: number
    lastMs: number
}

const tuning = (key: string): number => {
    const value = typeof window === "undefined" ? undefined : (window as any)[key]
    return typeof value === "number" && Number.isFinite(value) ? Math.max(0, value) : 1
}

export class LivingFirelight {
    private states = new Map<string, State>()

    /**
     * Write this frame's light for one fire. `target` is what the server says the light is;
     * `animate` is false off screen or by day, where the light simply follows the server.
     */
    update = (
        lights: LightsContainer,
        id: string,
        location: Coordinate,
        target: LightMeta,
        source: FirelightSource | undefined,
        animate: boolean,
        nowMs: number,
    ) => {
        let state = this.states.get(id)
        if (!state) {
            state = { radius: target.radius, intensity: target.intensity, lastMs: nowMs }
            this.states.set(id, state)
        }
        const dt = Math.max(0, Math.min(MAX_DT, (nowMs - state.lastMs) / 1000))
        state.lastMs = nowMs

        let radius = target.radius
        let intensity = target.intensity
        if (animate && source) {
            const k = 1 - Math.exp(-dt / (target.radius > state.radius ? GROW_TAU : SHRINK_TAU))
            state.radius += (target.radius - state.radius) * k
            state.intensity += (target.intensity - state.intensity) * k
            const breath = source.breath(nowMs) * tuning("firelightBreath")
            const flare = source.flareLevel(nowMs) * tuning("firelightFlare")
            radius = Math.max(0, state.radius * (1 + BREATH_RADIUS * breath + FLARE_RADIUS * flare))
            intensity = Math.max(0, Math.min(1, state.intensity * (1 + BREATH_INTENSITY * breath) + FLARE_INTENSITY * flare))
        } else {
            state.radius = target.radius
            state.intensity = target.intensity
        }

        const current = lights.get(id)
        if (current && current.radius === radius && current.intensity === intensity &&
            current.location.x === location.x && current.location.y === location.y) {
            return
        }
        lights.set(id, { entityId: id, location: { x: location.x, y: location.y }, radius, intensity })
    }

    remove = (id: string) => {
        this.states.delete(id)
    }
}
